import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { ArrowLeft } from 'lucide-react';
import { saveRiskProfile, submitPublicShare } from '../utils/api';
import SignaturePad from '../components/SignaturePad';
import ConsentNotice from '../components/ConsentNotice';
import { buildConsent, CONSENT_PURPOSE } from '../utils/dpdp';

const CreateRiskProfile = ({ publicToken = null, onSubmitted = null }) => {
  const navigate = useNavigate();
  const [consentGiven, setConsentGiven] = useState(false);
  const [formData, setFormData] = useState({
    investor_name: '',
    investor_email: '',
    investor_phone: '',
    age: '',
    q1_age_group: '',
    q2_investment_horizon: '',
    q3_poor_performance_hold: '',
    q4_income_source: '',
    q5_investment_objective: '',
    q6_risk_comfort: '',
    q7_investment_experience: '',
    q8_market_volatility: '',
    q9_liquidity_needs: '',
    q10_portfolio_preference: '',
    signature_data_url: '',
    signature_place: '',
    signature_date: new Date().toISOString().split('T')[0]
  });

  const questions = {
    q1_age_group: {
      question: 'How old are you?',
      options: [
        { value: 'below_25', label: 'Below 25 years', points: 10 },
        { value: '25_35', label: '25-35 years', points: 8 },
        { value: '36_45', label: '36-45 years', points: 6 },
        { value: '46_55', label: '46-55 years', points: 4 },
        { value: 'above_55', label: 'Above 55 years', points: 2 }
      ]
    },
    q2_investment_horizon: {
      question: 'How many years away is your nearest financial goal?',
      options: [
        { value: 'less_1', label: 'Less than 1 year', points: 2 },
        { value: '1_3', label: '1-3 years', points: 4 },
        { value: '3_5', label: '3-5 years', points: 6 },
        { value: '5_10', label: '5-10 years', points: 8 },
        { value: 'more_10', label: 'More than 10 years', points: 10 }
      ]
    },
    q3_poor_performance_hold: {
      question: 'If your investment outlook is long-term (more than 5 years), how long will you hold on to a poorly performing portfolio before cashing in?',
      options: [
        { value: 'immediately', label: 'Cash in immediately', points: 2 },
        { value: '6_months', label: '6 months', points: 4 },
        { value: '1_year', label: '1 year', points: 6 },
        { value: '2_years', label: '2 years', points: 8 },
        { value: 'long_term', label: 'Hold for long-term despite fluctuations', points: 10 }
      ]
    },
    q4_income_source: {
      question: 'My current and future source of income are:',
      options: [
        { value: 'pension', label: 'Pension/Fixed income only', points: 2 },
        { value: 'salary_stable', label: 'Stable salary', points: 4 },
        { value: 'salary_bonus', label: 'Salary with performance bonus', points: 6 },
        { value: 'business', label: 'Business/Self-employed', points: 8 },
        { value: 'multiple', label: 'Multiple sources of income', points: 10 }
      ]
    },
    q5_investment_objective: {
      question: 'Choose your investment preference/Objective:',
      options: [
        { value: 'capital_preservation', label: 'Capital Preservation (Safety first)', points: 2 },
        { value: 'regular_income', label: 'Regular Income generation', points: 4 },
        { value: 'balanced', label: 'Balanced (Income + Growth)', points: 6 },
        { value: 'capital_growth', label: 'Capital Growth', points: 8 },
        { value: 'aggressive_growth', label: 'Aggressive Growth (Maximum returns)', points: 10 }
      ]
    },
    q6_risk_comfort: {
      question: 'How comfortable are you with investment risk?',
      options: [
        { value: 'very_uncomfortable', label: 'Very uncomfortable - prefer guaranteed returns', points: 2 },
        { value: 'somewhat_uncomfortable', label: 'Somewhat uncomfortable', points: 4 },
        { value: 'neutral', label: 'Neutral', points: 6 },
        { value: 'comfortable', label: 'Comfortable with moderate risk', points: 8 },
        { value: 'very_comfortable', label: 'Very comfortable - willing to take high risk', points: 10 }
      ]
    },
    q7_investment_experience: {
      question: 'What is your investment experience?',
      options: [
        { value: 'none', label: 'No experience', points: 2 },
        { value: 'limited', label: 'Limited (Less than 2 years)', points: 4 },
        { value: 'moderate', label: 'Moderate (2-5 years)', points: 6 },
        { value: 'experienced', label: 'Experienced (5-10 years)', points: 8 },
        { value: 'very_experienced', label: 'Very experienced (More than 10 years)', points: 10 }
      ]
    },
    q8_market_volatility: {
      question: 'If your investment loses 20% of its value in a short period, you would:',
      options: [
        { value: 'sell_all', label: 'Sell all investments immediately', points: 2 },
        { value: 'sell_some', label: 'Sell some to cut losses', points: 4 },
        { value: 'hold', label: 'Hold and wait for recovery', points: 6 },
        { value: 'buy_more', label: 'Buy more at lower prices', points: 10 }
      ]
    },
    q9_liquidity_needs: {
      question: 'When might you need this invested money?',
      options: [
        { value: 'anytime', label: 'May need anytime (high liquidity)', points: 2 },
        { value: 'within_year', label: 'Within 1 year', points: 4 },
        { value: '1_3_years', label: '1-3 years', points: 6 },
        { value: '3_5_years', label: '3-5 years', points: 8 },
        { value: 'long_term', label: 'Not needed for 5+ years', points: 10 }
      ]
    },
    q10_portfolio_preference: {
      question: 'What would be your ideal portfolio allocation?',
      options: [
        { value: 'all_fixed', label: '100% Fixed Deposits/Bonds', points: 1 },
        { value: 'mostly_fixed', label: '80% Fixed Income, 20% Equity', points: 3 },
        { value: 'balanced', label: '50% Fixed Income, 50% Equity', points: 5 },
        { value: 'mostly_equity', label: '20% Fixed Income, 80% Equity', points: 7 },
        { value: 'all_equity', label: '100% Equity', points: 10 }
      ]
    }
  };

  const calculateRiskProfile = () => {
    let totalScore = 0;
    const answers = {};

    Object.keys(questions).forEach(key => {
      const questionData = questions[key];
      const selectedValue = formData[key];
      const selectedOption = questionData.options.find(opt => opt.value === selectedValue);
      
      if (selectedOption) {
        totalScore += selectedOption.points;
        answers[key] = {
          question: questionData.question,
          answer: selectedOption.label,
          points: selectedOption.points
        };
      }
    });

    let riskProfile = '';
    if (totalScore <= 40) {
      riskProfile = 'Conservative';
    } else if (totalScore <= 70) {
      riskProfile = 'Moderate';
    } else {
      riskProfile = 'Aggressive';
    }

    return { totalScore, riskProfile, answers };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!consentGiven) {
      alert('Please provide consent under the Data Protection Notice before submitting.');
      return;
    }

    const { totalScore, riskProfile, answers } = calculateRiskProfile();
    
    const profileData = {
      ...formData,
      total_score: totalScore,
      risk_profile: riskProfile,
      answers: answers,
      consent: buildConsent(CONSENT_PURPOSE.risk_profile, { assisted: !publicToken }),
      created_at: new Date().toISOString()
    };

    try {
      if (publicToken) {
        const res = await submitPublicShare(publicToken, profileData);
        if (onSubmitted) onSubmitted(res.data);
        return;
      }
      const profileId = await saveRiskProfile(profileData);
      navigate(`/admin/risk-profiling/view/${profileId}`);
    } catch (err) {
      alert('Error saving risk profile: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
      <div className="max-w-4xl mx-auto">
        {!publicToken && (
          <Button
            variant="outline"
            onClick={() => navigate('/admin/risk-profiling')}
            className="mb-6"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Risk Profiles
          </Button>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Create Investor Risk Profile</CardTitle>
            <p className="text-sm text-slate-600">Fill in investor details and answer the questionnaire to determine risk profile</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Investor Information */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-4 text-blue-900">Investor Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="investor_name">Full Name *</Label>
                    <input
                      id="investor_name"
                      type="text"
                      value={formData.investor_name}
                      onChange={(e) => setFormData({ ...formData, investor_name: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="investor_email">Email *</Label>
                    <input
                      id="investor_email"
                      type="email"
                      value={formData.investor_email}
                      onChange={(e) => setFormData({ ...formData, investor_email: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="investor_phone">Phone *</Label>
                    <input
                      id="investor_phone"
                      type="tel"
                      value={formData.investor_phone}
                      onChange={(e) => setFormData({ ...formData, investor_phone: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="age">Age *</Label>
                    <input
                      id="age"
                      type="number"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      min="18"
                      max="100"
                    />
                  </div>
                </div>
              </div>

              {/* Risk Assessment Questions */}
              <div className="space-y-6">
                <h3 className="font-bold text-lg text-slate-900">Risk Assessment Questionnaire</h3>
                
                {Object.entries(questions).map(([key, data], index) => (
                  <div key={key} className="bg-slate-50 p-4 rounded-lg">
                    <Label className="text-base font-semibold text-slate-900 mb-3 block">
                      {index + 1}. {data.question} *
                    </Label>
                    <div className="space-y-2">
                      {data.options.map((option) => (
                        <label key={option.value} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white cursor-pointer border border-transparent hover:border-blue-200">
                          <input
                            type="radio"
                            name={key}
                            value={option.value}
                            checked={formData[key] === option.value}
                            onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                            className="w-4 h-4 text-blue-600"
                            required
                          />
                          <span className="text-slate-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Investor Signature */}
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg" data-testid="signature-section">
                <h3 className="font-bold text-lg mb-2 text-amber-900">Investor Declaration & Signature</h3>
                <p className="text-sm text-slate-700 mb-4">
                  I confirm that the answers provided above accurately reflect my current financial situation
                  and risk appetite. I understand this profile will be used for investment recommendations.
                </p>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="signature_place">Place</Label>
                    <input
                      id="signature_place"
                      type="text"
                      value={formData.signature_place}
                      onChange={(e) => setFormData({ ...formData, signature_place: e.target.value })}
                      placeholder="E.g., Kolkata"
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="signature-place-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="signature_date">Date</Label>
                    <input
                      id="signature_date"
                      type="date"
                      value={formData.signature_date}
                      onChange={(e) => setFormData({ ...formData, signature_date: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="signature-date-input"
                    />
                  </div>
                </div>

                <Label>Investor Signature</Label>
                <div className="mt-1">
                  <SignaturePad
                    value={formData.signature_data_url}
                    onChange={(dataUrl) => setFormData({ ...formData, signature_data_url: dataUrl })}
                    testIdPrefix="investor-signature"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Signature is optional here — leave blank if the investor will sign a printed copy offline.
                </p>
              </div>

              <ConsentNotice
                purpose={CONSENT_PURPOSE.risk_profile}
                checked={consentGiven}
                onChange={setConsentGiven}
                assisted={!publicToken}
                items={['Name, email, phone and age', 'Your risk questionnaire responses', 'Signature (if provided)']}
              />

              <div className="flex gap-4 pt-4">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {publicToken ? 'Submit Risk Profile' : 'Generate Risk Profile'}
                </Button>
                {!publicToken && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/admin/risk-profiling')}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateRiskProfile;
